import React, { useEffect } from 'react';
import cover1 from './cover1.png';
import { useHistory, useLocation } from 'react-router-dom';
import { getListingsById } from '../../../../ducks/marketplaceData.duck';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useConfiguration } from '../../../../context/configurationContext';
import { useRouteConfiguration } from '../../../../context/routeConfigurationContext';
import { useIntl } from 'react-intl';

import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';

import css from './DiscoverSection.module.css';

const BasicCarousel = ({ images, startAfter, history }) => {
  const [focusedImageIndex, setFocusedImageIndex] = React.useState(0);

  useEffect(() => {
    // Reset index when images change
    setFocusedImageIndex(0);

    // Random delay between 0 and 5000 ms
    const randomDelay = startAfter;
    let intervalId;
    const timeoutId = setTimeout(() => {
      setFocusedImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      intervalId = setInterval(() => {
        setFocusedImageIndex((prevIndex) => (prevIndex + 1) % images.length);
      }, 6000);
    }, randomDelay);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [images.length, startAfter]);

  if (!images || images.length === 0) return null;

  const currentImage = images[focusedImageIndex];
  const imageUrl = typeof currentImage === 'string' ? currentImage : currentImage?.image;
  const listingUrl = typeof currentImage === 'string' ? null : currentImage?.to;

  const handleImageClick = () => {
    if (listingUrl && history) {
      history.push(listingUrl);
    }
  };

  return (
    <div className={css.carousel}>
      <div
        className={css.arrowLeft}
        onClick={() =>
          setFocusedImageIndex((focusedImageIndex - 1 + images.length) % images.length)
        }
      >
        <KeyboardArrowLeftRoundedIcon className={css.arrow} />
      </div>
      <img
        src={imageUrl}
        className={css.carouselImage}
        alt="Carousel"
        onClick={handleImageClick}
        style={{ cursor: listingUrl ? 'pointer' : 'default' }}
      />
      <div
        className={css.arrowRight}
        onClick={() => setFocusedImageIndex((focusedImageIndex + 1) % images.length)}
      >
        <KeyboardArrowRightRoundedIcon className={css.arrow} />
      </div>
    </div>
  );
};

const DiscoverSectionComponent = ({ listings, history }) => {
  // Extract images and listing URLs from listings (same pattern as ListingsSection)
  const listingImages =
    listings
      ?.filter((l) => l?.images?.[0])
      .map((l) => ({
        to: `/l/${l?.attributes?.title?.replaceAll(' ', '-')}/${l.id.uuid}`,
        image: l?.images[0]?.attributes?.variants?.['listing-card-2x']?.url,
      }))
      .filter((item) => item.image) || [];
  const hasListingsImages = listingImages.length > 0;
  // Create three carousel image arrays from the listing images
  // Split the images into three groups for the three carousels
  const imagesPerCarousel = Math.ceil(listingImages.length / 3);
  const carousel1Images = listingImages.slice(0, imagesPerCarousel);
  const carousel2Images = listingImages.slice(imagesPerCarousel, imagesPerCarousel * 2);
  const carousel3Images = listingImages.slice(imagesPerCarousel * 2);

  // If we don't have enough images, duplicate or use fallback
  const getCarouselImages = (images, index) => {
    if (images.length >= 3) return images;
    if (images.length > 0) {
      // Duplicate images to fill carousel
      const duplicated = [...images];
      while (duplicated.length < 3) {
        duplicated.push(...images);
      }
      return duplicated.slice(0, 3);
    }
    return [];
  };

  const finalCarousel1Images = getCarouselImages(carousel1Images, 0);
  const finalCarousel2Images = getCarouselImages(carousel2Images, 1);
  const finalCarousel3Images = getCarouselImages(carousel3Images, 2);

  return (
    <div className={css.wrapper}>
      <div className={css.box1}>
        <div className={css.box1Left}>
          <img src={cover1} alt="Discover Section Cover" className={css.coverImage} />
        </div>
        <div className={css.box1Right}>
          {' '}
          <p className={css.box1Title}>The scans getting the most love right now.</p>
          <p className={css.box1Description}>
            Discover the scans everyone's talking about—fresh uploads, community favourites, and
            most-downloaded assets, all in one place. Updated daily with what's hot right now.
          </p>
          <button
            type="button"
            onClick={() => history.push('/s')}
            className={css.quickActionButton}
          >
            <b className={css.searchIcon}></b>
            <span>What's Hot Right Now!</span>
          </button>
        </div>
      </div>

      {hasListingsImages && (
        <div className={css.box2}>
          <p className={css.box2Title}>Just landed!</p>
          <p className={css.box2Description}>
            Our newest 3d scan assets are here — stadiums, science, and everything in between.
            <br />
            Dive in and discover what's fresh at Scanwow.
          </p>

          <div className={css.carouselsWrapper}>
            {finalCarousel1Images.length > 0 && (
              <BasicCarousel startAfter={1000} images={finalCarousel1Images} history={history} />
            )}
            {finalCarousel2Images.length > 0 && (
              <BasicCarousel startAfter={3000} images={finalCarousel2Images} history={history} />
            )}
            {finalCarousel3Images.length > 0 && (
              <BasicCarousel startAfter={2000} images={finalCarousel3Images} history={history} />
            )}
          </div>

          <button
            type="button"
            onClick={() => history.push('/l/new')}
            className={css.quickActionButton}
          >
            <b className={css.searchIcon}></b>
            <span>Start Selling Your Scans</span>
          </button>
        </div>
      )}
    </div>
  );
};

const EnhancedDiscoverSection = (props) => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const { currentUser, ...restOfProps } = props;

  return (
    <DiscoverSectionComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state.user;
  const { currentPageResultIds } = state.SearchPage || {};
  const listings = getListingsById(state, currentPageResultIds || []);

  return {
    currentUser,
    listings,
  };
};

const mapDispatchToProps = (dispatch) => ({});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const DiscoverSection = compose(connect(mapStateToProps, mapDispatchToProps))(
  EnhancedDiscoverSection
);

export default DiscoverSection;
